'use client';

import { useState, useEffect, useCallback } from 'react';
import yaml from 'js-yaml';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Copy, Check, Sparkles, FileText, Upload, X, ArrowRight, Download } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import { cn } from '@/lib/utils';
import { ToolPageIntro } from '../../components/seo/ToolPageIntro';
import { InformationalContent } from '../../components/seo/InformationalContent';
import { yamlToXmlContent } from '../../data/yaml-to-xml-content';

const sampleYaml = `person:
  name: John Doe
  age: 30
  city: New York
  isActive: true
  skills:
    - JavaScript
    - TypeScript
    - React
  address:
    street: 123 Main St
    zip: 10001`;

const sampleXml = `<?xml version="1.0" encoding="UTF-8"?>
<person>
  <name>John Doe</name>
  <age>30</age>
  <city>New York</city>
  <isActive>true</isActive>
  <skills>
    <skill>JavaScript</skill>
    <skill>TypeScript</skill>
    <skill>React</skill>
  </skills>
  <address>
    <street>123 Main St</street>
    <zip>10001</zip>
  </address>
</person>`;

function convertYamlToXml(yamlString: string, rootElement: string = 'root'): string {
    try {
        if (!yamlString.trim()) {
            return '';
        }

        // Parse YAML to JavaScript object
        const jsonObj = yaml.load(yamlString);

        // Convert JSON to XML
        return jsonToXml(jsonObj, rootElement);
    } catch (error) {
        throw new Error(`Error converting YAML to XML: ${error instanceof Error ? error.message : 'Invalid YAML'}`);
    }
}

function jsonToXml(obj: any, nodeName: string = 'item'): string {
    if (obj === null || obj === undefined) {
        return '';
    }

    // Handle arrays
    if (Array.isArray(obj)) {
        return obj.map(item => jsonToXml(item, nodeName)).join('');
    }

    // Handle objects
    if (typeof obj === 'object') {
        const attributes: Record<string, string> = {};
        const children: string[] = [];
        let textContent: string | null = null;

        // Process object properties
        for (const [key, value] of Object.entries(obj)) {
            if (key.startsWith('@')) {
                // Handle attributes
                attributes[key.substring(1)] = String(value);
            } else if (key === '#text') {
                // Handle text content
                textContent = String(value);
            } else if (Array.isArray(value)) {
                // Handle arrays
                children.push(jsonToXml(value, key));
            } else if (typeof value === 'object' && value !== null) {
                // Handle nested objects
                children.push(jsonToXml(value, key));
            } else {
                // Handle primitive values
                children.push(`<${key}>${escapeXmlValue(value)}</${key}>`);
            }
        }

        // Build attributes string
        const attrs = Object.entries(attributes)
            .map(([key, val]) => ` ${key}="${escapeXmlAttr(val)}"`)
            .join('');

        // Handle self-closing tag if no content
        if (children.length === 0 && textContent === null) {
            return `<${nodeName}${attrs}/>`;
        }

        // Build content
        const content = textContent !== null
            ? escapeXmlValue(textContent)
            : children.join('');

        return `<${nodeName}${attrs}>${content}</${nodeName}>`;
    }

    // Handle primitive values
    return `<${nodeName}>${escapeXmlValue(obj)}</${nodeName}>`;
}

function escapeXmlValue(value: any): string {
    if (value === null || value === undefined) {
        return '';
    }

    return String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&apos;');
}

function escapeXmlAttr(value: string): string {
    return escapeXmlValue(value).replace(/\n/g, '&#10;').replace(/\t/g, '&#9;');
}

export default function YamlToXmlTool() {
    const [input, setInput] = useState('');
    const [output, setOutput] = useState('');
    const [error, setError] = useState('');
    const [isCopied, setIsCopied] = useState(false);
    const [fileName, setFileName] = useState<string | null>(null);
    const [rootElement, setRootElement] = useState('root');
    const [isConverting, setIsConverting] = useState(false);
    const { toast } = useToast();

    const convertYaml = useCallback((yamlString: string) => {
        try {
            if (!yamlString.trim()) {
                setOutput('');
                setError('');
                return;
            }

            setIsConverting(true);
            const xmlString = convertYamlToXml(yamlString, rootElement);
            // Format XML with proper indentation
            const formattedXml = formatXml(xmlString);
            setOutput(formattedXml);
            setError('');
        } catch (err) {
            setError(`Error: ${err instanceof Error ? err.message : 'Invalid YAML'}`);
            setOutput('');
        } finally {
            setIsConverting(false);
        }
    }, [rootElement]);

    // Format XML with proper indentation
    function formatXml(xml: string): string {
        try {
            // This is a simple formatter - for more complex XML, consider using a proper XML formatter library
            const PADDING = '  ';
            const reg = /(>)(<)(\/*)/g;
            let pad = 0;

            // Add line breaks and indentation
            xml = xml.replace(reg, '$1\n$2$3');

            // Split into lines and process each line
            return xml.split('\n').map((node) => {
                let indent = 0;
                if (node.match(/.+<\/\w[^>]*>$/)) {
                    // Closing tag
                    pad--;
                    indent = pad;
                } else if (node.match(/^<\w[^>]*>[^<]*$/)) {
                    // Opening tag with no children
                    indent = pad;
                } else if (node.match(/^<\w[^>]*>.*<\/\w[^>]*>$/)) {
                    // Self-closing tag or tag with text content
                    indent = pad;
                } else if (node.match(/^<\w[^>]*>.*$/)) {
                    // Opening tag with children
                    indent = pad++;
                } else if (node.match(/^<\/\w/)) {
                    // Closing tag
                    pad--;
                    indent = pad;
                } else {
                    // Text content
                    indent = pad;
                }

                return PADDING.repeat(Math.max(0, indent)) + node.trim();
            }).join('\n');
        } catch (e) {
            // If formatting fails, return the original XML
            return xml;
        }
    }

    useEffect(() => {
        const timer = setTimeout(() => {
            convertYaml(input);
        }, 500); // Slightly longer debounce for YAML parsing

        return () => clearTimeout(timer);
    }, [input, rootElement, convertYaml]);

    const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setFileName(file.name);

        const reader = new FileReader();
        reader.onload = (event) => {
            const content = event.target?.result as string;
            setInput(content);
        };
        reader.readAsText(file);

        // Reset the file input
        e.target.value = '';
    };

    const handleCopy = () => {
        if (!output) return;
        navigator.clipboard.writeText(output);
        setIsCopied(true);
        toast({
            title: 'Copied to clipboard!',
            description: 'The XML has been copied to your clipboard.',
            variant: 'default',
        });
        setTimeout(() => setIsCopied(false), 2000);
    };

    const handleDownload = () => {
        if (!output) return;

        const blob = new Blob([output], { type: 'application/xml;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName ? fileName.replace(/\.\w+$/, '.xml') : 'converted_data.xml';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const handleClear = () => {
        setInput('');
        setOutput('');
        setError('');
        setFileName(null);
    };

    const loadSample = () => {
        setInput(sampleYaml);
        setFileName('sample.yaml');
    };

    return (
        <div className="container mx-auto px-6 sm:px-8 py-12 max-w-7xl">
            <div className="text-center mb-10 space-y-2">
                <div className="inline-flex items-center justify-center px-4 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-medium mb-3">
                    <Sparkles className="h-4 w-4 mr-2" />
                    Developer Tool
                </div>
                <ToolPageIntro title="YAML to XML Converter - Free Online Data Format Converter">
                Convert YAML data to XML format instantly with our free online converter. Perfect for configuration files and data serialization.
            </ToolPageIntro>
            </div>

            <div className="w-full max-w-7xl mx-auto mb-6">
                <div className="flex flex-col sm:flex-row justify-between items-center gap-4 p-4 bg-muted/50 rounded-lg">
                    <div className="flex items-center gap-4 w-full">
                        <div className="flex-1">
                            <label className="block text-sm font-medium text-muted-foreground mb-1">
                                Root Element Name
                            </label>
                            <input
                                type="text"
                                value={rootElement}
                                onChange={(e) => setRootElement(e.target.value)}
                                className="w-full bg-background border border-input rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-primary/50 focus:outline-none"
                                disabled={isConverting}
                            />
                        </div>

                        <div className="relative flex-1">
                            <label className="block text-sm font-medium text-muted-foreground mb-1">
                                Upload YAML File
                            </label>
                            <label className="flex items-center justify-center w-full px-4 py-2 bg-background border border-dashed border-input rounded-md cursor-pointer hover:bg-accent/50 transition-colors">
                                <Upload className="h-4 w-4 mr-2" />
                                <span className="text-sm truncate max-w-[200px]">
                                    {fileName || 'Choose a file...'}
                                </span>
                                <input
                                    type="file"
                                    accept=".yaml,.yml,application/x-yaml,text/yaml"
                                    className="hidden"
                                    onChange={handleFileUpload}
                                    disabled={isConverting}
                                />
                            </label>
                            {fileName && (
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setFileName(null);
                                        setInput('');
                                    }}
                                    className="mt-1 text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
                                    disabled={isConverting}
                                >
                                    <X className="h-3 w-3" />
                                    Remove file
                                </button>
                            )}
                        </div>
                    </div>

                    <div className="flex items-center gap-2 mt-4 sm:mt-0">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={loadSample}
                            className="gap-1.5"
                            disabled={isConverting}
                        >
                            <Sparkles className="h-3.5 w-3.5" />
                            Sample
                        </Button>
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={handleClear}
                            disabled={!input || isConverting}
                            className="text-muted-foreground hover:text-foreground"
                        >
                            Clear
                        </Button>
                    </div>
                </div>
            </div>

            <div className="w-full max-w-7xl mx-auto grid grid-cols-12 gap-2 lg:gap-6 items-stretch">
                <div className="col-span-12 lg:col-span-5 h-full flex flex-col">
                    <Card className="border-0 shadow-lg overflow-hidden flex-1 flex flex-col h-full">
                        <CardHeader className="bg-gradient-to-r from-primary/5 to-primary/10 border-b">
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                                <div>
                                    <CardTitle className="text-xl font-semibold">
                                        YAML Input
                                    </CardTitle>
                                    <CardDescription className="mt-1">
                                        {fileName || 'Paste your YAML here'}
                                    </CardDescription>
                                </div>
                                <div className="text-sm font-mono bg-muted/50 px-2 py-1 rounded">
                                    YAML
                                </div>
                            </div>
                        </CardHeader>
                        <CardContent className="p-0 flex-1 flex flex-col">
                            <div className="flex-1 flex flex-col">
                                <Textarea
                                    value={input}
                                    onChange={(e) => setInput(e.target.value)}
                                    placeholder={
                                        'Paste your YAML here...\n' +
                                        'person:\n' +
                                        '  name: John Doe\n' +
                                        '  age: 30\n' +
                                        '  city: New York\n' +
                                        '  skills:\n' +
                                        '    - JavaScript\n' +
                                        '    - TypeScript\n' +
                                        '    - React'
                                    }
                                    className="min-h-[400px] font-mono text-sm border-0 focus-visible:ring-0 focus-visible:ring-offset-0 rounded-none resize-none"
                                    spellCheck={false}
                                    disabled={isConverting}
                                />
                            </div>
                        </CardContent>
                    </Card>
                </div>

                <div className="col-span-12 lg:col-span-2 flex items-center justify-center my-4 lg:my-0">
                    <div className="flex flex-col items-center">
                        <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center text-primary">
                            <ArrowRight className="h-5 w-5" />
                        </div>
                        <p className="text-xs text-muted-foreground mt-2 text-center">
                            Convert to
                        </p>
                    </div>
                </div>

                <div className="col-span-12 lg:col-span-5 h-full flex flex-col">
                    <Card className="border-0 shadow-lg overflow-hidden flex-1 flex flex-col h-full">
                        <CardHeader className="bg-gradient-to-r from-primary/5 to-primary/10 border-b">
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                                <div>
                                    <CardTitle className="text-xl font-semibold">
                                        XML Output
                                    </CardTitle>
                                    <CardDescription className="mt-1">
                                        {fileName ?
                                            fileName.replace(/\.\w+$/, '.xml') :
                                            'Your XML will appear here'}
                                    </CardDescription>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={handleDownload}
                                        disabled={!output || isConverting}
                                        className="gap-1.5"
                                    >
                                        <Download className="h-3.5 w-3.5" />
                                        Download
                                    </Button>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={handleCopy}
                                        disabled={!output || isConverting}
                                        className={cn(
                                            'gap-1.5 transition-all',
                                            isCopied
                                                ? 'bg-green-500/10 border-green-500/20 text-green-600 hover:bg-green-500/20'
                                                : 'hover:bg-primary/5'
                                        )}
                                    >
                                        {isCopied ? (
                                            <Check className="h-4 w-4" />
                                        ) : (
                                            <Copy className="h-4 w-4" />
                                        )}
                                        {isCopied ? 'Copied!' : 'Copy'}
                                    </Button>
                                </div>
                            </div>
                        </CardHeader>
                        <CardContent className="p-0 flex-1 flex flex-col">
                            <div className="relative flex-1 flex flex-col min-h-[400px]">
                                <Textarea
                                    value={output}
                                    readOnly
                                    className={cn(
                                        'flex-1 font-mono text-sm border-0 bg-muted/20',
                                        'focus-visible:ring-0 focus-visible:ring-offset-0 rounded-none resize-none',
                                        error && 'text-destructive/70',
                                        'whitespace-pre' // Preserve formatting
                                    )}
                                    placeholder="Your XML will appear here..."
                                />
                                {!output && !error && (
                                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                        <p className="text-muted-foreground/50 text-sm text-center px-4">
                                            Your XML will appear here
                                        </p>
                                    </div>
                                )}
                                {error && (
                                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                        <p className="text-destructive/70 text-sm text-center px-4">
                                            {error}
                                        </p>
                                    </div>
                                )}
                                {isConverting && (
                                    <div className="absolute inset-0 flex items-center justify-center bg-background/80">
                                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                                    </div>
                                )}
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>

            {/* Informational Content */}
            <div className="mt-12">
                <InformationalContent
                    title={yamlToXmlContent.title}
                    description={yamlToXmlContent.description}
                    content={yamlToXmlContent.content}
                    faqs={yamlToXmlContent.faqs}
                    relatedTools={yamlToXmlContent.relatedTools}
                />
            </div>

            <Toaster />
        </div>
    );
}
